import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, ViewChild, AfterViewInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseHorizontalNavigationComponent,
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { filter, map, Subject, takeUntil } from 'rxjs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TimelineActivitiesComponent } from 'app/layout/common/timeline-activities/timeline-activities.component';
import { UpdatesRequiredComponent } from 'app/layout/common/updates-required/updates-required.component';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { isPlatformAdmin, isServiceUnit } from 'app/core/user/domain-roles';
import {
    AssistCompany,
    AssistContextService,
} from 'app/core/assist-context/assist-context.service';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
// TEMP: `AddUserButtonComponent` import removed while the
// `<add-user-button>` element is hidden from the topbar. To restore
// it, uncomment the import below AND the matching entry in the
// `imports: [...]` array below AND the markup in the template
// (search the template for `TEMP: Add user button`).
// import { AddUserButtonComponent } from 'app/layout/common/add-user-button/add-user-button.component';
import { QuickAddUserComponent } from 'app/layout/common/quick-add-user/quick-add-user.component';
import { VoiceCallsComponent } from 'app/layout/common/voice-calls/voice-calls.component';
import { VideoCallsComponent } from 'app/layout/common/video-calls/video-calls.component';
import { McpDrawerComponent } from 'app/layout/common/mcp-drawer/mcp-drawer.component';
import { WorkspaceParticlesComponent } from 'app/layout/common/workspace-particles/workspace-particles.component';
import { TopbarLiveFeedComponent } from 'app/layout/common/topbar-live-feed/topbar-live-feed.component';

@Component({
    selector: 'modern-layout',
    standalone: true,
    templateUrl: './modern.component.html',
    encapsulation: ViewEncapsulation.None,
    imports: [
    CommonModule,
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatMenuModule,
    /*  LanguagesComponent, */
    FuseFullscreenComponent,
    /* SearchComponent,
    ShortcutsComponent, */
    /* NotificationsComponent, */
    UserComponent,
    RouterLink,
    RouterOutlet,
    QuickChatComponent,
    MatSidenavModule,
    MatTooltipModule,
    MessagesComponent,
    // TEMP: AddUserButtonComponent removed from imports while the
    // template element is hidden (see top-of-file comment).
    // AddUserButtonComponent,
    QuickAddUserComponent,
    TimelineActivitiesComponent,
    UpdatesRequiredComponent,
    VoiceCallsComponent,
        // TEMP: VideoCallsComponent removed from imports while the
        // <video-calls> elements are hidden (see HTML comments).
        // VideoCallsComponent,
    McpDrawerComponent,
    WorkspaceParticlesComponent,
    TopbarLiveFeedComponent,
],
})
export class ModernLayoutComponent implements OnInit, OnDestroy, AfterViewInit {
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    currentTopbarTime = '';
    currentTopbarDate = '';
    agentEnabled = false;
    /**
     * The current URL the router is on. Used to resolve which nav
     * item the menu trigger should display (icon + label) and to
     * mark that item as "active" inside the dropdown.
     */
    currentUrl = '';
    /** Admin/Production company-assist picker state (top of page menu). */
    canUseCompanyPicker = false;
    assistCompanies: AssistCompany[] = [];
    assistCompany: AssistCompany | null = null;
    private _clockInterval: any;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    @ViewChild('profileDrawer') profileDrawer: any;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _userService: UserService,
        private _assistContext: AssistContextService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    /**
     * Getter for logo URL — business-account company logo when present
     * (Trader / Ind. Sales / Assistant inheriting org branding via login).
     */
    get logoUrl(): string {
        if (this.user?.company_logo) {
            return this.user.company_logo;
        }
        return 'images/logo/dp_logo.png';
    }

    /**
     * Getter for logo alt text
     */
    get logoAlt(): string {
        if (this.user?.company_name) {
            return this.user.company_name;
        }
        return 'District Printing';
    }

    /**
     * The horizontal-nav item that matches the current URL. Powers
     * the topbar menu trigger so it shows the page the user is on
     * (icon + name) instead of a generic "Menu" label. Falls back
     * to the first nav item, then to a stub, so the button always
     * renders something legible.
     */
    get currentNavItem(): any {
        const items: any[] = this.navigation?.horizontal || [];
        if (!items.length) {
            return { id: 'menu', title: 'Menu', icon: 'heroicons_outline:squares-2x2' };
        }
        const url = this.currentUrl || this._router.url || '';
        // Exact-link match wins; otherwise prefix match (e.g. /orders
        // matches /orders/list). Falls back to the first item so the
        // trigger always has a label to show.
        const exact = items.find((i) => i?.link && i.link === url);
        if (exact) return exact;
        const prefix = items.find(
            (i) => i?.link && i.link !== '/' && url.startsWith(i.link),
        );
        if (prefix) return prefix;
        return items[0];
    }

    /**
     * Active-item check used by the dropdown so the currently visited
     * row is visually emphasized.
     */
    isActiveNavItem(item: any): boolean {
        const url = this.currentUrl || this._router.url || '';
        if (!item?.link) return false;
        return item.link === url ||
            (item.link !== '/' && url.startsWith(item.link));
    }

    get selfCompanyLabel(): string {
        return (this.user as any)?.company_name || 'District Printing';
    }

    get selfUserName(): string {
        return this.user?.name || this.user?.email || 'Signed-in user';
    }

    get selfUserRole(): string {
        return this.user?.role || 'Staff';
    }

    get selfCompanyLogo(): string {
        return this.user?.company_logo || 'images/logo/dp_logo.png';
    }

    get selfProfileImage(): string | null {
        return this.user?.avatar || (this.user as any)?.profile_image || null;
    }

    get assistCompanyLabel(): string {
        return this._assistContext.displayLabel(this.assistCompany, this.selfCompanyLabel);
    }

    get menuTooltip(): string {
        if (this.canUseCompanyPicker && this.assistCompany) {
            return `Assisting ${this.assistCompanyLabel} · Switch page or company`;
        }
        if (this.canUseCompanyPicker) {
            return 'Switch page or company';
        }
        return 'Switch page';
    }

    companyMenuLabel(company: AssistCompany): string {
        return this._assistContext.displayLabel(company);
    }

    companyUserName(company: AssistCompany): string {
        const name = `${company?.first_name || ''} ${company?.last_name || ''}`.trim();
        return name || company?.email || 'Account owner';
    }

    companyUserRole(company: AssistCompany): string {
        return company?.role || company?.kind || 'Business account';
    }

    companyProfileImage(company: AssistCompany): string | null {
        return company?.profile_image || company?.avatar || null;
    }

    initials(value: string): string {
        return (value || '?')
            .split(/\s+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part.charAt(0).toUpperCase())
            .join('') || '?';
    }

    isAssistCompanyActive(company: AssistCompany): boolean {
        if (!this.assistCompany || !company) return false;
        return String(this.assistCompany.id) === String(company.id);
    }

    onSelectAssistCompany(company: AssistCompany | null): void {
        this._assistContext.selectCompany(company);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this.agentEnabled = false;
        window.addEventListener('agent1-toggle', this._onAgentToggle as EventListener);

        this._updateTopbarTime();
        this._clockInterval = setInterval(() => this._updateTopbarTime(), 1000);

        // Get user role from localStorage for navigation title mapping
        let userRole: string | null = null;
        try {
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                const userData = JSON.parse(userDataString);
                userRole = userData?.role;
            }
        } catch (e) {
            console.error('Error parsing userData from localStorage:', e);
        }

        // Subscribe to navigation data
        this._navigationService.navigation$
        .pipe(
            takeUntil(this._unsubscribeAll),
            map((navigation: Navigation) => {
            // Title map: Admin sees "Users", others see "Customers"
            const titleMap: {[key: string]: string} = {
                'dashboard': 'Operations',
                'add-order': 'New Order', 
                'customers': userRole === 'Admin' ? 'Users' : 'Customers',
                'image-gallery': 'Gallery' 
            };
            
            // Helper function to apply title mapping
            const applyTitleMap = (item: any) => titleMap[item.id] 
                ? { ...item, title: titleMap[item.id] }
                : item;
            
            return {
                ...navigation,
                // Apply title mapping to both horizontal (desktop) and default (mobile) navigation
                horizontal: navigation.horizontal.map(applyTitleMap),
                default: navigation.default.map(applyTitleMap)
            };
            })
        )
        .subscribe((navigation: Navigation) => {
            this.navigation = navigation;
            console.log('Navigation data:', this.navigation);
        });

        // Subscribe to the user service to get trader's company logo
        // and initialize Admin/Production company-assist context.
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                this.canUseCompanyPicker = isServiceUnit(user);
                this.agentEnabled = this._isAgentUiEnabled();
                this._assistContext.initForUser(user);
            });

        this._assistContext.companies$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((companies) => {
                this.assistCompanies = companies || [];
            });

        this._assistContext.company$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((company) => {
                this.assistCompany = company;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Track router URL so the topbar menu trigger can show the
        // current page (icon + name) and so the dropdown can flag the
        // active route. Seed with the current URL because the very
        // first NavigationEnd fires before this component is created.
        this.currentUrl = this._router.url || '';
        this._router.events
            .pipe(
                filter((ev): ev is NavigationEnd => ev instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe((ev: NavigationEnd) => {
                this.currentUrl = ev.urlAfterRedirects || ev.url || '';
            });

        try {
            window.addEventListener('open-main-navigation', () => {
                try { this.toggleNavigation('mainNavigation'); } catch {}
            });
        } catch {}
    }

    ngAfterViewInit(): void {
        // The topbar "+ user" entry point is now owned by
        // `<add-user-button>` (which handles `open-add-user-drawer`
        // and `prefill-quick-add-user` window events directly and
        // routes through `AddUserDrawerHostService`).  The layout
        // only retains the profile-drawer hook here.
        try {
            window.addEventListener('open-profile-drawer', () => {
                try { console.log('[Profile][Layout:Modern] open'); this.profileDrawer?.open?.(); } catch (e) { console.error('[Profile][Layout:Modern] fail', e); }
            });
        } catch {}
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        if (this._clockInterval) clearInterval(this._clockInterval);
        window.removeEventListener('agent1-toggle', this._onAgentToggle as EventListener);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    private _isAgentUiEnabled(): boolean {
        return isPlatformAdmin(this.user)
            && localStorage.getItem('agent1.enabled') === 'true';
    }

    private _onAgentToggle = (e: CustomEvent) => {
        this.agentEnabled = !!e?.detail?.enabled && isPlatformAdmin(this.user);
    };

    private _updateTopbarTime(): void {
        const now = new Date();
        // Stacked clock — weekday + month/day on a small muted line,
        // time on a large bold line below. Splitting into two fields
        // lets the template style each line independently.
        const date = now.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });
        const time = now.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        });
        this.currentTopbarDate = date;
        this.currentTopbarTime = time;
    }
}
